import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../../api/client";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { useLiveTimer } from "../../hooks/useTimer";
import { enqueue, newClientOfflineId } from "../../lib/offlineQueue";
import { useOffline } from "../../contexts/OfflineContext";
import { ArrowLeft, Play, Square, Flag } from "lucide-react";
import { MOBILE } from "../../constants/testIds";
import { toast } from "sonner";

const WORK_TYPES = ["FW", "OW", "drafting", "search", "travel"];

export default function MobileJobDetail() {
    const { jobId } = useParams();
    const nav = useNavigate();
    const loc = useLocation();
    const initialType = new URLSearchParams(loc.search).get("type") || "FW";
    const [job, setJob] = useState(null);
    const [workType, setWorkType] = useState(initialType);
    const [running, setRunning] = useState(null);
    const [flagged, setFlagged] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const { online, refreshCount } = useOffline();

    useEffect(() => {
        (async () => {
            const [j, r] = await Promise.all([
                api.get(`/jobs/${jobId}`).then((x) => x.data),
                api.get("/v1/time-entries/running").then((x) => x.data).catch(() => null),
            ]);
            setJob(j);
            if (r && r.job_id === jobId) setRunning(r);
        })();
    }, [jobId]);

    const t = useLiveTimer(running?.start_time);

    const start = async () => {
        setBusy(true);
        const oid = newClientOfflineId();
        const body = { job_id: jobId, work_type: workType, client_offline_id: oid };
        try {
            if (!online) throw new Error("offline");
            const { data } = await api.post("/v1/time-entries/start-timer", body);
            setRunning(data);
            toast.success("Timer started. Good luck out there.");
        } catch (_) {
            await enqueue({ url: "/v1/time-entries/start-timer", method: "POST", body, client_offline_id: oid });
            const now = new Date().toISOString();
            setRunning({ id: oid, start_time: now, work_type: workType, job_id: jobId, _offline: true });
            toast.message("Queued offline — will sync when back on signal");
            refreshCount();
        } finally {
            setBusy(false);
        }
    };

    const stop = async () => {
        if (!running) return;
        setBusy(true);
        const body = { is_variation: flagged, variation_note: note || undefined };
        try {
            if (!online || running._offline) throw new Error("offline");
            const { data } = await api.post(`/v1/time-entries/${running.id}/stop`, body);
            nav(`/m/stopped/${data.id}`);
        } catch (_) {
            await enqueue({
                url: `/v1/time-entries/${running.id}/stop`,
                method: "POST",
                body: { ...body, end_time: new Date().toISOString() },
            });
            toast.message("Stop queued offline — will sync when back on signal");
            refreshCount();
            nav(`/m/stopped/${running.id}?offline=1`);
        } finally {
            setBusy(false);
        }
    };

    if (!job) return null;
    const isRunning = !!running;

    return (
        <div data-testid={MOBILE.jobDetail} className="px-5 pt-4 pb-32">
            <button onClick={() => nav("/m/today")} className="flex items-center gap-1 text-sm text-muted mb-4">
                <ArrowLeft size={16} /> Today
            </button>

            <div className="eyebrow">Job {job.job_number || "—"}</div>
            <h1 className="text-2xl font-extrabold tracking-[-0.03em] mt-1 leading-tight">{job.address}</h1>
            {job.description && <p className="text-sm text-muted mt-2">{job.description}</p>}

            <div className="mt-5 grid grid-cols-3 gap-3">
                <FactCell label="Status" value={<StatusBadge status={job.status} />} />
                <FactCell label="Types" value={<span className="text-xs">{(job.job_types || []).join(", ") || "—"}</span>} />
                <FactCell label="Quoted" value={<span className="num text-sm">{job.quoted_amount ? `$${job.quoted_amount.toLocaleString()}` : "—"}</span>} />
            </div>

            {isRunning ? (
                <div className="mt-6 rounded-2xl p-5 text-white text-center" style={{ background: "linear-gradient(150deg,#268254,#14563A)" }}>
                    <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.14em] opacity-90">
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Running · {running.work_type}
                    </div>
                    <div data-testid={MOBILE.timer} className="num text-5xl mt-3 tracking-wider">{t}</div>
                    <div className="text-xs opacity-80 mt-2">
                        Started {running.start_time ? new Date(running.start_time).toLocaleTimeString("en-AU", { timeZone: "Australia/Brisbane", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                </div>
            ) : (
                <>
                    <div className="mt-6 py-6 text-center">
                        <div className="num text-5xl text-muted tracking-wider">00:00:00</div>
                    </div>
                    <div className="eyebrow mb-2">Work type</div>
                    <div className="flex flex-wrap gap-2 mb-6">
                        {WORK_TYPES.map((w) => (
                            <button
                                key={w}
                                data-testid={`work-type-${w}`}
                                onClick={() => setWorkType(w)}
                                className={
                                    "km-chip " +
                                    (w === workType
                                        ? "km-chip-navy-solid"
                                        : "km-chip-grey")
                                }
                                style={{ cursor: "pointer" }}
                            >
                                {w}
                            </button>
                        ))}
                    </div>
                </>
            )}

            <div className={`mt-4 km-card p-4 ${flagged ? "ring-2 ring-[color:var(--km-red)]" : ""}`}>
                <label className="flex items-center gap-3">
                    <input
                        type="checkbox"
                        checked={flagged}
                        onChange={(e) => setFlagged(e.target.checked)}
                        data-testid={MOBILE.variationToggle}
                        className="w-5 h-5 accent-[color:var(--km-red)]"
                    />
                    <div className="flex-1">
                        <div className="text-sm font-medium flex items-center gap-1">
                            <Flag size={14} className="text-[color:var(--km-red)]" /> Extras on this job?
                        </div>
                        <div className="text-xs text-muted">Tap. Office will follow up.</div>
                    </div>
                </label>
                {flagged && (
                    <textarea
                        data-testid={MOBILE.variationNote}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note (what changed)"
                        rows={2}
                        className="mt-3 w-full rounded-lg border border-km px-3 py-2 text-sm"
                    />
                )}
            </div>

            {isRunning ? (
                <button
                    data-testid={MOBILE.stopBtn}
                    onClick={stop}
                    disabled={busy}
                    className="mt-5 w-full h-14 rounded-2xl text-white font-bold flex items-center justify-center gap-2 text-base"
                    style={{ background: "linear-gradient(150deg,#E23F35,#A02722)" }}
                >
                    <Square size={16} fill="currentColor" /> {busy ? "Saving…" : "Stop & Save"}
                </button>
            ) : (
                <button
                    data-testid={MOBILE.startBtn}
                    onClick={start}
                    disabled={busy}
                    className="mt-5 w-full h-14 rounded-2xl text-white font-bold flex items-center justify-center gap-2 text-base"
                    style={{ background: "linear-gradient(150deg,#268254,#14563A)" }}
                >
                    <Play size={16} fill="currentColor" /> {busy ? "Starting…" : "Start Timer"}
                </button>
            )}
        </div>
    );
}

function FactCell({ label, value }) {
    return (
        <div className="km-card p-2.5">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted">{label}</div>
            <div className="mt-1">{value}</div>
        </div>
    );
}
